import { MessageActionType } from '../types'
import { createToken } from '../utils'

interface ITabManagerOptions {
  channelName: string
  onPrompt: (event?: Event) => void
  onIdle: (event?: Event) => void
  onActive: (remote?: boolean) => void
  start: (remote?: boolean) => void
  reset: (remote?: boolean) => void
}

enum RegistryState {
  PROMPTED,
  ACTIVE,
  IDLE,
}

interface IMessage {
  action: MessageActionType
  token: string
  data?: any
}

export class TabManager {
  private channel: BroadcastChannel
  private options: ITabManagerOptions

  public token: string = createToken()
  public registry: Map<string, RegistryState> = new Map()
  public allIdle = false
  public isLastActive = false

  constructor(options: ITabManagerOptions) {
    const { channelName } = options
    this.options = options

    this.channel = new BroadcastChannel(channelName)
    this.registry.set(this.token, RegistryState.ACTIVE)

    this.channel.addEventListener('message', (message: MessageEvent<IMessage>) => {
      const { action, token } = message.data
      switch (action) {
        case MessageActionType.REGISTER:
          this.registry.set(token, RegistryState.IDLE)
          break
        case MessageActionType.DEREGISTER:
          this.registry.delete(token)
          break
        case MessageActionType.IDLE:
          this.idle(token)
          break
        case MessageActionType.ACTIVE:
          this.active(token)
          break
        case MessageActionType.PROMPT:
          this.prompt(token)
          break
        case MessageActionType.START:
          this.start(token)
          break
        case MessageActionType.RESET:
          break
      }
    })

    this.send(MessageActionType.REGISTER)
  }

  public prompt(token: string = this.token) {
    this.registry.set(token, RegistryState.PROMPTED)
    const values = Array.from(this.registry.values())
    const isPrompted = values.every((v) => v === RegistryState.PROMPTED)

    if (token === this.token) {
      this.send(MessageActionType.PROMPT)
    }

    if (isPrompted) {
      this.options.onPrompt()
    }
  }

  public idle(token: string = this.token) {
    this.registry.set(token, RegistryState.IDLE)
    const values = Array.from(this.registry.values())
    const isIdle = values.every((v) => v === RegistryState.IDLE)

    if (token === this.token) {
      this.send(MessageActionType.IDLE)
    }

    if (isIdle) {
      this.allIdle = true
      this.options.onIdle()
    }
  }

  public active(token: string = this.token) {
    this.allIdle = false
    this.registry.set(token, RegistryState.ACTIVE)
    const values = Array.from(this.registry.values())
    const isActive = values.some((v) => v === RegistryState.ACTIVE)

    if (token === this.token) {
      this.send(MessageActionType.ACTIVE)
    }

    if (isActive) {
      this.options.onActive(true)
    }

    // Set last active
    this.isLastActive = token === this.token
  }

  public start(token: string = this.token) {
    this.allIdle = false
    this.registry.set(token, RegistryState.ACTIVE)
    if (token === this.token) {
      this.send(MessageActionType.START)
    } else {
      this.options.start(true)
    }

    // Set last active
    this.isLastActive = token === this.token
  }

  reset(token = this.token) {
    this.allIdle = false
    this.registry.set(token, RegistryState.ACTIVE)
    if (token === this.token) {
      this.send(MessageActionType.RESET)
    } else {
      this.options.reset(true)
    }
    // Set last active
    this.isLastActive = token === this.token
  }

  message(data: any) {
    try {
      this.channel.postMessage({
        action: MessageActionType.MESSAGE,
        token: this.token,
        data,
      })
      // eslint-disable-next-line no-empty
    } catch {}
  }

  send(action: MessageActionType) {
    try {
      this.channel.postMessage({ action, token: this.token })
      // eslint-disable-next-line no-empty
    } catch {}
  }

  close() {
    this.send(MessageActionType.DEREGISTER)
    this.channel.close()
  }
}
